"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpDown, Search, SearchX, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import BasicPagination from "@/components/common/BasicPagination";
import {
  RESOURCE_FAMILY_ICONS,
  RESOURCE_FAMILY_ORDER,
} from "@/components/resource/search/family-meta";
import {
  getResourceSearchUrlQuery,
  parseResourceSearchFilter,
  resourceSearchSortOptions,
  type ResourceSearchFilters,
  type ResourceSearchResponse,
} from "@/lib/fetch/resource-search";
import { useDebounce } from "@/lib/hook/use-debounce";
import type { ResourceSearchFamily } from "@/db/definitions";
import { twMerge } from "tailwind-merge";
import ResourceSearchCard from "./ResourceSearchCard";

const POPULAR_SEARCH_KEYS = ["1", "2", "3", "4", "5", "6"] as const;

type Props = {
  locale: string;
  initialFilters: ResourceSearchFilters;
};

const searchParamsToObject = (params: URLSearchParams) => {
  const object: Record<string, string | string[]> = {};

  params.forEach((_, key) => {
    const values = params.getAll(key);
    object[key] = values.length > 1 ? values : values[0];
  });

  return object;
};

const ResourceSearchView: React.FC<Props> = ({ locale, initialFilters }) => {
  const t = useTranslations("resource-search");

  const searchParams = useSearchParams();
  const loadedInitialUrlFilters = useRef(false);
  const resultsRef = useRef<HTMLDivElement>(null);

  const [filters, setFilters] = useState(initialFilters);
  const [inputValue, setInputValue] = useState(initialFilters.query);
  const [canSyncUrl, setCanSyncUrl] = useState(false);

  useEffect(() => {
    if (loadedInitialUrlFilters.current) {
      return;
    }

    loadedInitialUrlFilters.current = true;

    const parsed = parseResourceSearchFilter(
      searchParamsToObject(new URLSearchParams(searchParams)),
    );

    setFilters(parsed);
    setInputValue(parsed.query);
    setCanSyncUrl(true);
  }, [searchParams]);

  useEffect(() => {
    if (!canSyncUrl) {
      return;
    }

    const { params } = getResourceSearchUrlQuery(filters);
    const nextSearch = params.toString();

    if (nextSearch !== window.location.search.slice(1)) {
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}`,
      );
    }
  }, [filters, canSyncUrl]);

  const debouncedInput = useDebounce(inputValue, 300);

  useEffect(() => {
    setFilters((current) => {
      if (current.query === debouncedInput) {
        return current;
      }

      return { ...current, query: debouncedInput, page: 1 };
    });
  }, [debouncedInput]);

  const { data, isLoading, isPlaceholderData } = useQuery({
    queryKey: ["resource", "search", filters],
    enabled: canSyncUrl,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const { params } = getResourceSearchUrlQuery(filters);
      params.append("locale", locale);

      const response = await fetch(
        `/api/site/public/resource/search?${params.toString()}`,
        { method: "GET" },
      );

      const empty: ResourceSearchResponse = {
        items: [],
        count: 0,
        familyCounts: {},
      };

      if (!response.ok) {
        return empty;
      }

      const body = (await response.json()) as {
        success: boolean;
        data: ResourceSearchResponse;
      };

      if (!body.success) {
        return empty;
      }

      return body.data;
    },
  });

  const toggleFamily = (family: ResourceSearchFamily) => {
    setFilters((current) => {
      const active = current.families.includes(family);
      const families = active
        ? current.families.filter((entry) => entry !== family)
        : [...current.families, family];

      return { ...current, families, page: 1 };
    });
  };

  const clearFamilies = () => {
    setFilters((current) => ({ ...current, families: [], page: 1 }));
  };

  const onPageChange = (page: number) => {
    setFilters((current) => ({ ...current, page }));
    resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onPopularSearch = (query: string) => {
    setInputValue(query);
    setFilters((current) => ({ ...current, query, families: [], page: 1 }));
  };

  const items = data?.items ?? [];
  const count = data?.count ?? 0;
  const familyCounts = data?.familyCounts ?? {};
  const totalPages = Math.ceil(count / filters.pageSize);
  const showSkeletons = isLoading || (isPlaceholderData && items.length === 0);

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(locale),
    [locale],
  );

  return (
    <div className="container flex flex-col gap-6 px-4 pb-16 pt-5">
      <section className="relative overflow-hidden rounded-3xl bg-secondary p-5 sm:p-7 lg:p-8">
        <CanalHousesArt className="pointer-events-none absolute -right-4 bottom-0 hidden h-24 w-auto lg:block" />

        <div className="relative flex flex-col gap-5 lg:max-w-[calc(100%-15rem)]">
          <label
            htmlFor="resource-search-input"
            className="text-xs font-semibold uppercase tracking-wide text-secondary-foreground/70"
          >
            {t("view.search-label")}
          </label>

          <div className="flex items-center gap-2 rounded-2xl bg-background p-2 shadow-sm">
            <Search className="ml-2 size-5 flex-none text-muted-foreground" />
            <input
              id="resource-search-input"
              type="search"
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              placeholder={t("view.input-placeholder")}
              autoComplete="off"
              className="h-10 min-w-0 flex-1 border-0 bg-transparent text-sm outline-none placeholder:text-muted-foreground focus:ring-0 sm:h-11 sm:text-base [&::-webkit-search-cancel-button]:hidden"
            />
            {inputValue && (
              <button
                type="button"
                onClick={() => setInputValue("")}
                aria-label={t("view.clear")}
                className="flex size-9 flex-none items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="size-4" />
              </button>
            )}
          </div>

          <div className="-mx-1 flex flex-row gap-2 overflow-x-auto px-1 py-1 scrollbar-none sm:flex-wrap sm:overflow-visible">
            <FamilyChip
              label={t("view.families.all")}
              active={filters.families.length === 0}
              onClick={clearFamilies}
            />
            {RESOURCE_FAMILY_ORDER.map((family) => {
              const Icon = RESOURCE_FAMILY_ICONS[family];
              const familyCount = familyCounts[family];

              return (
                <FamilyChip
                  key={family}
                  label={t(`view.families.${family}`)}
                  icon={<Icon className="size-3.5" />}
                  count={
                    familyCount !== undefined
                      ? numberFormatter.format(familyCount)
                      : undefined
                  }
                  active={filters.families.includes(family)}
                  onClick={() => toggleFamily(family)}
                />
              );
            })}
          </div>
        </div>
      </section>

      <div
        ref={resultsRef}
        className="flex scroll-mt-20 flex-wrap items-center justify-between gap-3"
      >
        <p className="text-sm text-muted-foreground sm:text-base">
          {t.rich("view.found-results", {
            count,
            b: (chunks) => (
              <b className="font-semibold text-foreground">{chunks}</b>
            ),
          })}
        </p>

        <Select
          value={filters.sort}
          onValueChange={(value) =>
            setFilters((current) => ({
              ...current,
              sort: value as ResourceSearchFilters["sort"],
              page: 1,
            }))
          }
        >
          <SelectTrigger aria-label={t("view.sort-placeholder")} className="w-fit text-sm font-medium sm:w-[190px]">
            <div className="flex flex-row items-center gap-2">
              <ArrowUpDown className="size-4" />
              <p className="hidden sm:block">
                <SelectValue placeholder={t("view.sort-placeholder")} />
              </p>
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {resourceSearchSortOptions.map((option) => (
                <SelectItem key={option} value={option}>
                  {t(`view.sort.${option}`)}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      {showSkeletons ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {[...Array(9)].map((_, index) => (
            <Skeleton key={index} className="h-56 w-full rounded-2xl" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-5 rounded-3xl border border-dashed border-border px-4 py-16 text-center">
          <span className="flex size-14 items-center justify-center rounded-2xl bg-secondary">
            <SearchX className="size-7" />
          </span>
          <div className="flex flex-col gap-2">
            <h2 className="text-lg font-semibold">
              {t("view.no-results.title")}
            </h2>
            <p className="max-w-md text-sm text-muted-foreground">
              {t("view.no-results.description")}
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {POPULAR_SEARCH_KEYS.map((key) => {
              const query = t(`view.popular.${key}`);

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => onPopularSearch(query)}
                  className="rounded-full border border-border bg-background px-4 py-2 text-xs font-medium transition-colors hover:border-zinc-300 hover:bg-muted"
                >
                  {query}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div
          className={twMerge(
            "grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3",
            isPlaceholderData && "opacity-60",
          )}
        >
          {items.map((item) => (
            <ResourceSearchCard key={item.key} item={item} />
          ))}
        </div>
      )}

      {totalPages > 1 && !showSkeletons && (
        <BasicPagination
          currentPage={filters.page}
          totalPages={totalPages}
          perPage={filters.pageSize}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
};

export default ResourceSearchView;

type FamilyChipProps = {
  label: string;
  icon?: React.ReactNode;
  count?: string;
  active: boolean;
  onClick: () => void;
};

const FamilyChip: React.FC<FamilyChipProps> = ({
  label,
  icon,
  count,
  active,
  onClick,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={twMerge(
        "flex min-h-9 flex-none items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors",
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-transparent bg-background text-foreground hover:bg-background/70",
      )}
    >
      {icon}
      <span>{label}</span>
      {count !== undefined && (
        <span
          className={twMerge(
            "rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none tabular-nums",
            active
              ? "bg-secondary text-secondary-foreground"
              : "bg-muted text-muted-foreground",
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
};

const CanalHousesArt: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <svg
      viewBox="0 0 220 96"
      aria-hidden="true"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* step gable */}
      <path
        className="fill-primary"
        d="M8 96V38h6v-8h6v-8h8v8h6v8h6v58Z"
      />
      <rect className="fill-secondary" x="18" y="44" width="8" height="10" />
      <rect className="fill-secondary" x="18" y="62" width="8" height="10" />
      {/* bell gable */}
      <path
        className="fill-primary"
        d="M48 96V34c0-10 8-14 14-14s14 4 14 14v62Z"
      />
      <rect className="fill-secondary" x="56" y="42" width="12" height="12" />
      <rect className="fill-secondary" x="56" y="64" width="12" height="12" />
      {/* neck gable */}
      <path
        className="fill-primary"
        d="M84 96V40h8V22h12v18h8v56Z"
      />
      <rect className="fill-secondary" x="92" y="48" width="12" height="12" />
      <rect className="fill-secondary" x="92" y="68" width="12" height="12" />
      {/* spout gable */}
      <path className="fill-primary" d="M120 96V44l14-16 14 16v52Z" />
      <rect className="fill-secondary" x="128" y="52" width="12" height="12" />
      <rect className="fill-secondary" x="128" y="72" width="12" height="12" />
      {/* tall step gable */}
      <path
        className="fill-primary"
        d="M156 96V30h8v-8h8v-8h8v8h8v8h8v66Z"
      />
      <rect className="fill-secondary" x="168" y="38" width="8" height="10" />
      <rect className="fill-secondary" x="184" y="38" width="8" height="10" />
      <rect className="fill-secondary" x="168" y="58" width="8" height="10" />
      <rect className="fill-secondary" x="184" y="58" width="8" height="10" />
    </svg>
  );
};
